import React, { useState } from 'react'
import { Download } from 'lucide-react'
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx'

interface ClaimsSupportDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  searchTerm?: string;
  matchesWildcard?: (text: string, pattern: string) => boolean;
  showToggle?: boolean;
  onAddToInput?: (text: string) => void;
  onCopyResult?: () => void;
  showToast?: (message: string) => void;
  showHeader?: boolean;
}

const ClaimsSupportDisplay: React.FC<ClaimsSupportDisplayProps> = ({
  result,
  theme = 'light',
  searchTerm = '',
  matchesWildcard,
  showToggle = false,
  onAddToInput,
  onCopyResult,
  showToast,
  showHeader = true
}) => {
  const [showStructured, setShowStructured] = useState(false)
  const [expandedClaims, setExpandedClaims] = useState(new Set())
  const [expandedFeatures, setExpandedFeatures] = useState(new Set())

  // Check if the result looks like a claims support map object
  const isClaimsSupportResult = (data) => {
    if (!data || typeof data !== 'object') return false
    
    const keys = Object.keys(data)
    if (keys.length === 0) return false
    
    // Check for new claim_00001 structure format
    let claimFormatEntries = 0
    keys.forEach(key => {
      // Check for claim_00001 format keys
      if (/^claim_\d+$/.test(key)) {
        const entry = data[key]
        if (entry && typeof entry === 'object') {
          // Check if it has claim elements with citations, reference_numerals, support_explanation
          const entryKeys = Object.keys(entry)
          entryKeys.forEach(elementKey => {
            const element = entry[elementKey]
            if (element && typeof element === 'object' && 
                (element.citations || element.reference_numerals || element.support_explanation)) {
              claimFormatEntries++
            }
          })
        }
      }
    })
    
    // If we found claim_00001 format structures, consider it claims support
    if (claimFormatEntries > 0) {
      return true
    }
    
    // Check for numbered claim structure (legacy new format)
    let validEntries = 0
    keys.forEach(key => {
      // Must be a number key
      if (!/^\d+$/.test(key)) return
      
      const entry = data[key]
      if (entry && typeof entry === 'object') {
        // Check if it has the claims support structure with features (NOT content field)
        if (entry.content) return // This is claims with numerals, not claims support
        
        const featureKeys = Object.keys(entry)
        featureKeys.forEach(featureKey => {
          const feature = entry[featureKey]
          if (feature && typeof feature === 'object' && 
              (feature.characteristics || feature.children !== undefined)) {
            validEntries++
          }
        })
      }
    })
    
    // If we found claim-like structures, consider it claims support
    if (validEntries > 0) {
      return true
    }
    
    // Fallback: Check for legacy claim_support_map property
    if (data.claim_support_map && typeof data.claim_support_map === 'object') {
      const supportMap = data.claim_support_map
      const legacyKeys = Object.keys(supportMap)
      if (legacyKeys.length === 0) return false
      
      // Check if entries have the legacy structure
      let legacyValidEntries = 0
      legacyKeys.forEach(key => {
        const entry = supportMap[key]
        if (entry && typeof entry === 'object' && 
            (entry.support_paragraphs !== undefined || 
             entry.elements !== undefined)) {
          legacyValidEntries++
        }
      })
      
      return legacyValidEntries / legacyKeys.length >= 0.5
    }
    
    return false
  }

  if (!isClaimsSupportResult(result)) {
    return null // Not a claims support result, don't render this component
  }

  // Handle legacy, numbered, and claim_00001 data structures
  const isLegacyFormat = result.claim_support_map && typeof result.claim_support_map === 'object'
  const claimSupportMap = isLegacyFormat ? result.claim_support_map : result
  
  // Check if this is the claim_00001 format
  const isClaimFormat = !isLegacyFormat && (() => {
    const keys = Object.keys(result)
    return keys.some(key => /^claim_\d+$/.test(key))
  })()
  
  // Check if this is the numbered format (numbered claims with features)
  const isNumberedFormat = !isLegacyFormat && !isClaimFormat && (() => {
    const keys = Object.keys(result)
    if (keys.length === 0) return false
    
    let validEntries = 0
    keys.forEach(key => {
      const entry = result[key]
      if (entry && typeof entry === 'object') {
        const featureKeys = Object.keys(entry)
        featureKeys.forEach(featureKey => {
          const feature = entry[featureKey]
          if (feature && typeof feature === 'object' && 
              (feature.characteristics || feature.children !== undefined)) {
            validEntries++
          }
        })
      }
    })
    
    return validEntries > 0
  })()

  const toggleClaim = (claimId) => {
    const newExpanded = new Set(expandedClaims)
    if (newExpanded.has(claimId)) {
      newExpanded.delete(claimId)
    } else {
      newExpanded.add(claimId)
    }
    setExpandedClaims(newExpanded)
  }

  const toggleFeature = (featureKey) => {
    const newExpanded = new Set(expandedFeatures)
    if (newExpanded.has(featureKey)) {
      newExpanded.delete(featureKey)
    } else {
      newExpanded.add(featureKey)
    }
    setExpandedFeatures(newExpanded)
  }

  const formatClaimName = (claimId) => {
    if (claimId.startsWith('claim_')) {
      // Convert claim_00001 to Claim 1
      const match = claimId.match(/claim_(\d+)/)
      if (match) {
        return `Claim ${parseInt(match[1], 10)}`
      }
    }
    return claimId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const formatFeatureName = (name) => {
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const formatElementName = (name) => {
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  // Custom export function for claims support hierarchical data
  const exportClaimsSupportToDocx = async () => {
    try {
      const children: Paragraph[] = [];

      // Title
      children.push(
        new Paragraph({
          text: "Claims Support Analysis",
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Recursive function to add feature paragraphs
      const addFeatureParagraphs = (featureKey: string, feature: any, level: number = 0) => {
        const cleanName = formatFeatureName(featureKey);
        const indent = level * 360; // 360 twips = 0.25 inch

        // Feature name with numeral
        const featureRuns: TextRun[] = [];
        if (feature.numeral) {
          featureRuns.push(new TextRun({ text: `${feature.numeral} - `, bold: true }));
        }
        featureRuns.push(new TextRun({ text: cleanName, bold: true }));

        children.push(
          new Paragraph({
            children: featureRuns,
            spacing: { before: 200, after: 100 },
            indent: { left: indent },
          })
        );

        // Characteristics
        if (feature.characteristics && feature.characteristics.length > 0) {
          feature.characteristics.forEach((char: any) => {
            const charRuns: TextRun[] = [
              new TextRun({ text: `• ${char.feature}` }),
            ];
            if (char.citation) {
              charRuns.push(new TextRun({ text: ` [${char.citation}]`, bold: true }));
            }
            children.push(
              new Paragraph({
                children: charRuns,
                spacing: { after: 50 },
                indent: { left: indent + 360 },
              })
            );
          });
        }

        // Recursively add children
        if (feature.children && Object.keys(feature.children).length > 0) {
          Object.entries(feature.children).forEach(([childKey, childFeature]) => {
            addFeatureParagraphs(childKey, childFeature, level + 1);
          });
        }
      };

      // Process each claim based on format
      const claims = isLegacyFormat ? result.claim_support_map : result;
      Object.entries(claims).forEach(([claimKey, claimData]: [string, any]) => {
        // Claim header
        const claimTitle = isClaimFormat ? formatClaimName(claimKey) : `Claim ${claimKey}`;
        children.push(
          new Paragraph({
            text: claimTitle,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        );

        if (isClaimFormat) {
          // Handle claim_00001 format
          Object.entries(claimData).forEach(([elementKey, elementData]: [string, any]) => {
            // Element name
            children.push(
              new Paragraph({
                text: formatElementName(elementKey),
                heading: HeadingLevel.HEADING_3,
                spacing: { before: 200, after: 100 },
              })
            );

            // Support explanation
            if (elementData.support_explanation) {
              children.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Support: ", bold: true }),
                    new TextRun({ text: elementData.support_explanation }),
                  ],
                  spacing: { after: 100 },
                  indent: { left: 360 },
                })
              );
            }

            // Reference numerals
            if (elementData.reference_numerals && elementData.reference_numerals.length > 0) {
              children.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Reference Numerals: ", bold: true }),
                    new TextRun({ text: elementData.reference_numerals.join(', ') }),
                  ],
                  spacing: { after: 100 },
                  indent: { left: 360 },
                })
              );
            }

            // Citations
            if (elementData.citations && elementData.citations.length > 0) {
              children.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Citations: ", bold: true }),
                    new TextRun({ text: elementData.citations.join(', ') }),
                  ],
                  spacing: { after: 100 },
                  indent: { left: 360 },
                })
              );
            }
          });
        } else {
          // Handle numbered format or legacy format
          Object.entries(claimData).forEach(([featureKey, feature]) => {
            addFeatureParagraphs(featureKey, feature, 0);
          });
        }
      });

      // Create Word document
      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'claims-support-analysis.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (showToast) {
        showToast('Claims support analysis exported to Word document');
      }
    } catch (error) {
      console.error('Error exporting claims support to DOCX:', error);
      if (showToast) {
        showToast('Error exporting claims support analysis');
      }
    }
  }

  const highlightText = (text, searchTerm) => {
    if (!text || !searchTerm) return text
    
    if (matchesWildcard) {
      const pattern = searchTerm.replace(/\*/g, '.*')
      const regex = new RegExp(`(${pattern})`, 'gi')
      return text.replace(regex, '<mark class="bg-yellow-300 text-black">$1</mark>')
    } else {
      const regex = new RegExp(`(${searchTerm})`, 'gi')
      return text.replace(regex, '<mark class="bg-yellow-300 text-black">$1</mark>')
    }
  }

  const shouldHighlight = (text, searchTerm) => {
    if (!text || !searchTerm) return false
    
    if (matchesWildcard) {
      const pattern = searchTerm.replace(/\*/g, '.*')
      const regex = new RegExp(pattern, 'i')
      return regex.test(text)
    } else {
      return text.toLowerCase().includes(searchTerm.toLowerCase())
    }
  }

  // New format rendering for hierarchical claims support
  const renderNewFormatFeature = (featureKey, feature, claimNum, level = 0) => {
    const featureId = `${claimNum}-${featureKey}-${level}`
    const isExpanded = expandedFeatures.has(featureId)
    const hasChildren = feature.children && Object.keys(feature.children).length > 0
    const indentClass = level > 0 ? `ml-${Math.min(level * 4, 16)}` : ''

    return (
      <div key={featureId} className={`${indentClass} ${level > 0 ? 'border-l-2 border-gray-300 pl-4 ml-2' : ''}`}>
        <div className={`p-3 rounded-lg mb-2 transition-all duration-200 ${
          theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'
        }`}>
          <div className="flex items-start gap-3">
            {/* Expand/Collapse Button */}
            {hasChildren && (
              <button
                onClick={() => toggleFeature(featureId)}
                className={`mt-1 p-1 rounded transition-colors ${
                  theme === 'dark' ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
                }`}
              >
                <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
            
            <div className="flex-1">
              {/* Feature Name and Numeral */}
              <div className={`font-semibold text-lg mb-2 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                <span 
                  dangerouslySetInnerHTML={{ 
                    __html: searchTerm ? highlightText(formatFeatureName(featureKey), searchTerm) : formatFeatureName(featureKey)
                  }}
                />
                {feature.numeral && (
                  <span className={`ml-3 font-bold text-xl ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                    {feature.numeral}
                  </span>
                )}
              </div>

              {/* Characteristics */}
              {feature.characteristics && feature.characteristics.length > 0 && (
                <div className="mb-2">
                  <div className={`text-xs font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Support Features:
                  </div>
                  <div className="space-y-2">
                    {feature.characteristics.map((char, idx) => (
                      <div key={idx} className={`p-3 rounded-md border-l-4 ${
                        theme === 'dark' 
                          ? 'bg-gray-600 border-blue-400' 
                          : 'bg-blue-50 border-blue-400'
                      }`}>
                        <div className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                          <span 
                            dangerouslySetInnerHTML={{ 
                              __html: searchTerm ? highlightText(char.feature, searchTerm) : char.feature
                            }}
                          />
                        </div>
                        {char.citation && (
                          <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} flex items-center`}>
                            <span className="mr-1">📖</span>
                            <span className={`font-mono px-1 rounded ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
                              {char.citation}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="ml-4">
            {Object.entries(feature.children).map(([childKey, childFeature]) =>
              renderNewFormatFeature(childKey, childFeature, claimNum, level + 1)
            )}
          </div>
        )}
      </div>
    )
  }

  const renderNewFormatClaim = (claimNum, claimData) => {
    const claimId = `claim-${claimNum}`
    const isClaimExpanded = expandedClaims.has(claimId)
    
    return (
      <div 
        key={claimId}
        className={`border rounded-lg ${theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`}
      >
        <div 
          className={`p-4 cursor-pointer hover:${theme === 'dark' ? 'bg-gray-750' : 'bg-gray-50'} rounded-t-lg`}
          onClick={() => toggleClaim(claimId)}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className={`font-bold text-xl ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                <span 
                  dangerouslySetInnerHTML={{ 
                    __html: searchTerm ? highlightText(`Claim ${claimNum}`, searchTerm) : `Claim ${claimNum}`
                  }}
                />
              </div>
            </div>
            <div className={`ml-4 transform transition-transform ${isClaimExpanded ? 'rotate-180' : ''}`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
        
        {isClaimExpanded && (
          <div className={`border-t p-4 ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
            <div className="space-y-2">
              {Object.entries(claimData).map(([featureKey, feature]) =>
                renderNewFormatFeature(featureKey, feature, claimNum, 0)
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderClaimFormatClaim = (claimKey, claimData) => {
    const claimId = claimKey
    const isClaimExpanded = expandedClaims.has(claimId)
    
    return (
      <div 
        key={claimId}
        className={`border rounded-lg ${theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`}
      >
        <div 
          className={`p-4 cursor-pointer hover:${theme === 'dark' ? 'bg-gray-750' : 'bg-gray-50'} rounded-t-lg`}
          onClick={() => toggleClaim(claimId)}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className={`font-bold text-xl ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                <span 
                  dangerouslySetInnerHTML={{ 
                    __html: searchTerm ? highlightText(formatClaimName(claimKey), searchTerm) : formatClaimName(claimKey)
                  }}
                />
              </div>
            </div>
            <div className={`ml-4 transform transition-transform ${isClaimExpanded ? 'rotate-180' : ''}`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
        
        {isClaimExpanded && (
          <div className={`border-t p-4 ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
            <div className="space-y-4">
              {Object.entries(claimData).map(([elementKey, elementData]) =>
                renderClaimFormatElement(elementKey, elementData, claimKey)
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderClaimFormatElement = (elementKey, elementData, claimKey) => {
    const elementId = `${claimKey}-${elementKey}`
    
    return (
      <div key={elementId} className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
        {/* Element Name */}
        <div className={`font-semibold text-lg mb-3 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
          <span 
            dangerouslySetInnerHTML={{ 
              __html: searchTerm ? highlightText(formatElementName(elementKey), searchTerm) : formatElementName(elementKey)
            }}
          />
        </div>

        {/* Support Explanation */}
        {elementData.support_explanation && (
          <div className="mb-3">
            <div className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              📄 Support Explanation:
            </div>
            <div className={`p-3 rounded-md border-l-4 ${theme === 'dark' ? 'bg-gray-600 border-green-400 text-gray-200' : 'bg-green-50 border-green-400 text-gray-800'}`}>
              <span 
                dangerouslySetInnerHTML={{ 
                  __html: searchTerm ? highlightText(elementData.support_explanation, searchTerm) : elementData.support_explanation
                }}
              />
            </div>
          </div>
        )}

        {/* Reference Numerals */}
        {elementData.reference_numerals && elementData.reference_numerals.length > 0 && (
          <div className="mb-3">
            <div className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              🔢 Reference Numerals:
            </div>
            <div className="flex flex-wrap gap-2">
              {elementData.reference_numerals.map((numeral, idx) => (
                <span
                  key={idx}
                  className={`px-2 py-1 rounded-full text-sm font-mono font-bold ${
                    theme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {numeral}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Citations */}
        {elementData.citations && elementData.citations.length > 0 && (
          <div>
            <div className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              📚 Citations:
            </div>
            <div className="flex flex-wrap gap-2">
              {elementData.citations.map((citation, idx) => (
                <span
                  key={idx}
                  className={`px-2 py-1 rounded-full text-sm font-mono ${
                    theme === 'dark' ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'
                  }`}
                >
                  {citation}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const matchesSearch = (claimId, claim) => {
    if (!searchTerm || !matchesWildcard) return true
    
    const name = formatClaimName(claimId)
    const elements = claim.elements ? claim.elements.join(' ') : ''
    const paragraphs = claim.support_paragraphs ? claim.support_paragraphs.join(' ') : ''
    
    return matchesWildcard(name, searchTerm) ||
           matchesWildcard(elements, searchTerm) ||
           matchesWildcard(paragraphs, searchTerm)
  }

  // Filter claims based on the format
  const filteredClaims = isLegacyFormat 
    ? Object.entries(claimSupportMap).filter(([claimId, claim]) => matchesSearch(claimId, claim))
    : isClaimFormat 
      ? Object.entries(claimSupportMap).filter(([claimKey, claimData]) => {
          // For claim_00001 format, check if any elements match search
          if (!searchTerm) return true
          return Object.entries(claimData).some(([elementKey, elementData]) => {
            const formattedClaimName = formatClaimName(claimKey)
            const formattedElementName = formatElementName(elementKey)
            const supportExplanation = elementData.support_explanation || ''
            const citations = elementData.citations ? elementData.citations.join(' ') : ''
            const referenceNumerals = elementData.reference_numerals ? elementData.reference_numerals.join(' ') : ''
            return shouldHighlight(formattedClaimName, searchTerm) ||
                   shouldHighlight(formattedElementName, searchTerm) ||
                   shouldHighlight(supportExplanation, searchTerm) ||
                   shouldHighlight(citations, searchTerm) ||
                   shouldHighlight(referenceNumerals, searchTerm)
          })
        })
      : Object.entries(claimSupportMap).filter(([claimNum, claimData]) => {
          // For numbered format, check if any features match search
          if (!searchTerm) return true
          return Object.entries(claimData).some(([featureKey, feature]) => {
            const formattedName = formatFeatureName(featureKey)
            const characteristics = feature.characteristics ? 
              feature.characteristics.map(char => char.feature || '').join(' ') : ''
            return shouldHighlight(`Claim ${claimNum}`, searchTerm) ||
                   shouldHighlight(formattedName, searchTerm) ||
                   shouldHighlight(characteristics, searchTerm)
          })
        })

  const renderClaimSupport = (claimId, claim) => {
    const isExpanded = expandedClaims.has(claimId)
    const hasElements = claim.elements && claim.elements.length > 0
    const hasParagraphs = claim.support_paragraphs && claim.support_paragraphs.length > 0

    return (
      <div
        key={claimId}
        className={`p-4 rounded-lg border transition-all duration-200 ${
          theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
        }`}
      >
        <div className="flex items-center justify-between mb-3">
          <div className={`flex items-center gap-3`}>
            <div className={`text-lg font-bold px-3 py-1 rounded-full ${
              theme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
            }`}>
              {formatClaimName(claimId)}
            </div>
            
            {/* Support Status Indicators */}
            <div className="flex gap-2">
              {hasParagraphs && (
                <span className={`text-xs px-2 py-1 rounded-full ${
                  theme === 'dark' ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
                }`}>
                  {claim.support_paragraphs.length} Paragraph{claim.support_paragraphs.length !== 1 ? 's' : ''}
                </span>
              )}
              {hasElements && (
                <span className={`text-xs px-2 py-1 rounded-full ${
                  theme === 'dark' ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'
                }`}>
                  {claim.elements.length} Element{claim.elements.length !== 1 ? 's' : ''}
                </span>
              )}
              {!hasParagraphs && !hasElements && (
                <span className={`text-xs px-2 py-1 rounded-full ${
                  theme === 'dark' ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800'
                }`}>
                  No Support Found
                </span>
              )}
            </div>
          </div>

          {(hasElements || hasParagraphs) && (
            <button
              onClick={() => toggleClaim(claimId)}
              className={`p-1 rounded transition-colors ${
                theme === 'dark' ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
              }`}
            >
              <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}
        </div>

        {isExpanded && (
          <div className="space-y-4">
            {/* Support Paragraphs */}
            {hasParagraphs && (
              <div>
                <h4 className={`font-semibold mb-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  📄 Supporting Paragraphs
                </h4>
                <div className="flex flex-wrap gap-2">
                  {claim.support_paragraphs.map((paragraph, idx) => (
                    <span
                      key={idx}
                      className={`px-3 py-1 rounded-full text-sm font-mono ${
                        theme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {paragraph}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Elements */}
            {hasElements && (
              <div>
                <h4 className={`font-semibold mb-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  🔧 Claim Elements
                </h4>
                <div className="space-y-2">
                  {claim.elements.map((element, idx) => (
                    <div
                      key={idx}
                      className={`p-2 rounded text-sm ${
                        theme === 'dark' ? 'bg-gray-600 text-gray-300' : 'bg-gray-50 text-gray-700'
                      }`}
                    >
                      {element}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  const renderFormattedSupport = () => {
    if (searchTerm && filteredClaims.length === 0) {
      return (
        <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
          <div className="text-lg mb-2">No claims found</div>
          <div className="text-sm">Try adjusting your search term or using wildcards (* for any characters)</div>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {filteredClaims.map(([claimId, claim]) =>
          isLegacyFormat 
            ? renderClaimSupport(claimId, claim)
            : isClaimFormat 
              ? renderClaimFormatClaim(claimId, claim)
              : renderNewFormatClaim(claimId, claim)
        )}
        
        {/* Analysis metrics hidden but data objects preserved for later use */}
        {filteredClaims.length > 0 && false && (
          <div className={`mt-6 p-4 rounded-lg ${
            theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-blue-50 border border-blue-200'
          }`}>
            <div className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-blue-900'}`}>
              📊 Support Summary
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                <div className="font-medium">Total Claims</div>
                <div className={`text-lg font-bold ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                  {Object.keys(claimSupportMap).length}
                </div>
              </div>
              <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                <div className="font-medium">Supported</div>
                <div className={`text-lg font-bold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                  {isLegacyFormat
                    ? Object.values(claimSupportMap).filter((c: any) =>
                        (c.support_paragraphs && c.support_paragraphs.length > 0) ||
                        (c.elements && c.elements.length > 0)
                      ).length
                    : isClaimFormat
                      ? Object.values(claimSupportMap).filter(claimData => 
                          Object.values(claimData).some(element => 
                            (element.citations && element.citations.length > 0) ||
                            (element.reference_numerals && element.reference_numerals.length > 0) ||
                            (element.support_explanation && element.support_explanation.trim())
                          )
                        ).length
                      : Object.values(claimSupportMap).filter(claimData => 
                          Object.values(claimData).some(feature => 
                            feature.characteristics && feature.characteristics.length > 0
                          )
                        ).length
                  }
                </div>
              </div>
              <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                <div className="font-medium">Unsupported</div>
                <div className={`text-lg font-bold ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
                  {isLegacyFormat
                    ? Object.values(claimSupportMap).filter((c: any) =>
                        (!c.support_paragraphs || c.support_paragraphs.length === 0) &&
                        (!c.elements || c.elements.length === 0)
                      ).length
                    : isClaimFormat
                      ? Object.values(claimSupportMap).filter(claimData => 
                          !Object.values(claimData).some(element => 
                            (element.citations && element.citations.length > 0) ||
                            (element.reference_numerals && element.reference_numerals.length > 0) ||
                            (element.support_explanation && element.support_explanation.trim())
                          )
                        ).length
                      : Object.values(claimSupportMap).filter(claimData => 
                          !Object.values(claimData).some(feature => 
                            feature.characteristics && feature.characteristics.length > 0
                          )
                        ).length
                  }
                </div>
              </div>
              <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                <div className="font-medium">Showing</div>
                <div className={`text-lg font-bold ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`}>
                  {filteredClaims.length}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderStructuredData = () => {
    return (
      <pre className={`text-sm whitespace-pre-wrap font-mono p-4 rounded-md  overflow-y-auto ${
        theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
      }`}>
        {JSON.stringify(result, null, 2)}
      </pre>
    )
  }

  return (
    <div className={showHeader ? "space-y-3" : ""}>
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            🔗 Claims Support Map
          </div>
          {showToggle && (
            <div className="flex items-center gap-2">
              {onAddToInput && (
                <button
                  onClick={() => {
                    onAddToInput(typeof result === 'object' ? JSON.stringify(result, null, 2) : result);
                    if (showToast) showToast('Result added to chat input!');
                  }}
                  className={`text-xs py-1 px-3 rounded-md transition-colors ${
                    theme === 'dark' ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  Add to Chat
                </button>
              )}
              <button
                onClick={() => {
                  if (onCopyResult) onCopyResult();
                }}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Copy
              </button>
              <button
                onClick={exportClaimsSupportToDocx}
                className={`text-xs py-1 px-3 rounded-md transition-colors flex items-center gap-1 ${
                  theme === 'dark' ? 'bg-green-600 text-white hover:bg-green-500' : 'bg-green-500 text-white hover:bg-green-600'
                }`}
                title="Export claims support analysis as Word document"
              >
                <Download className="w-3 h-3" />
                Export DOCX
              </button>
              <button
                onClick={() => setShowStructured(!showStructured)}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  showStructured
                    ? theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                    : theme === 'dark' ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {showStructured ? 'Show Formatted' : 'Show Raw Data'}
              </button>
              <button
                onClick={() => {
                  setExpandedClaims(new Set())
                  setExpandedFeatures(new Set())
                }}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Collapse All
              </button>
              <button
                onClick={() => {
                  // Expand all claims
                  const allClaimIds = isLegacyFormat
                    ? Object.keys(claimSupportMap).map(claimId => claimId)
                    : isClaimFormat
                      ? Object.keys(claimSupportMap).map(claimKey => claimKey)
                      : Object.keys(claimSupportMap).map(claimNum => `claim-${claimNum}`)
                  setExpandedClaims(new Set(allClaimIds))

                  // Expand all features for numbered format only
                  if (isNumberedFormat) {
                    const allFeatureIds: string[] = []
                    Object.entries(claimSupportMap).forEach(([claimNum, claimData]) => {
                      const collectFeatureIds = (features: any, claimNum: string, level = 0) => {
                        Object.entries(features).forEach(([featureKey, feature]: [string, any]) => {
                          const featureId = `${claimNum}-${featureKey}-${level}`
                          allFeatureIds.push(featureId)
                          if (feature.children && Object.keys(feature.children).length > 0) {
                            collectFeatureIds(feature.children, claimNum, level + 1)
                          }
                        })
                      }
                      collectFeatureIds(claimData, claimNum)
                    })
                    setExpandedFeatures(new Set(allFeatureIds))
                  }
                }}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Expand All
              </button>
            </div>
          )}
        </div>
      )}

      <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
        {showStructured ? renderStructuredData() : renderFormattedSupport()}
      </div>
    </div>
  )
}

export default ClaimsSupportDisplay